import axiosInstance from "../../utils/axios";
import { v4 as uuidv4 } from "uuid";
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const generateUUID = () => {
  return uuidv4();
};

export const uploadDocumentWithUUID = async (
  imageId,
  file,
  type = "delivery"
) => {
  if (!(file instanceof File)) {
    console.error("❌ File is not a valid File object:", file);
    throw new Error("Invalid file object provided");
  }
  const formData = new FormData();
  formData.append("image_id", imageId);
  formData.append("type", type);
  formData.append("file", file);

  try {
    const res = await axiosInstance.post(
      `/file_upload/upload/document?image_id=${imageId}&type=${type}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return res;
  } catch (error) {
    console.error(
      "❌ Error uploading document:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const createDelivery = async (data) => {
  const res = await axiosInstance.post(`/projects/delivery`, data);
  return res;
};

export const createDeliveryWithInventory = async (data, projectId) => {
  const res = await axiosInstance.post(`/projects/delivery-with-inventory?project_id=${projectId}`, data);
  return res;
};

export const createDeliveryLaydown = async (data) => {
  const res = await axiosInstance.post(`/projects/delivery-laydown`, data);
  return res;
};

export const updateInventory = async (
  laydownId,
  deliveryLaydownId,
  deliveryId,
  inventoryData
) => {
  try {
    const res = await axiosInstance.post(
      `/projects/inventory_update?laydown_id=${laydownId}&received=True&delivery_laydown_id=${deliveryLaydownId}&delivery_id=${deliveryId}`,
      inventoryData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    return res;
  } catch (error) {
    console.error(
      "❌ Error updating inventory:",
      error.response?.data || error.message
    );
    throw error;
  }
};

export const saveCustomResponses = async (deliveryId, customResponsesData) => {
  try {
    if (!Array.isArray(customResponsesData)) {
      throw new Error('Custom responses data must be an array');
    }

    customResponsesData.forEach(response => {
      if (!response.custom_input_id || !response.field_name || !response.value) {
        throw new Error('Each custom response must have custom_input_id, field_name, and value');
      }
      response.custom_input_id = Number(response.custom_input_id);
    });

    const res = await axiosInstance.post(
      `/projects/delivery/custom_responses?delivery_id=${deliveryId}`,
      customResponsesData
    );
    return res;
  } catch (error) {
    console.error('Error saving custom responses:', error);
    throw error;
  }
};

export const updateCustomResponses = async (deliveryId, customResponsesData) => {
  try {
    if (!Array.isArray(customResponsesData)) {
      throw new Error('Custom responses data must be an array');
    }

    customResponsesData.forEach(response => {
      if (!response.custom_input_delivery_id || response.response === undefined || response.response === null) {
        throw new Error('Each custom response must have custom_input_delivery_id and response');
      }
      response.custom_input_delivery_id = Number(response.custom_input_delivery_id);
      response.delivery_id = deliveryId;
    });


    const res = await axiosInstance.post(
      `/projects/delivery/custom_responses?delivery_id=${deliveryId}`,
      customResponsesData
    );
    return res;
  } catch (error) {
    console.error('Error updating custom responses:', error);
    throw error;
  }
};

export const getScopeDropdown = async (id, scopeId) => {
  const res = await axiosInstance.get( scopeId ? `/laydown-scope/v2/unassigned-tracker-rows?laydown_id=${id}&scope=${scopeId}` : `/laydown-scope/v2/unassigned-tracker-rows?laydown_id=${id}`);
  return res;
};

export const getScopeListData = async (id) => {
  const res = await axiosInstance.get(`/laydown-scope/v2/get_scopes?laydown_id=${id}`);
  return res;
};

export const getScopeList = async (id) => {
  const res = await axiosInstance.get(`/laydown-scope/v2/unassigned-tracker-rows?laydown_id=${id}`);
  return res;
};

export const getDeliveries = async () => {
  const res = await axiosInstance.get(`/projects/delivery`);
  return res;
};

export const getDeliveriesDownloads = async () => {
  const res = await axiosInstance.get(`/projects/get_all_data_delivery`);
  return res;
};

export const getBomDeliveryLaydowns = async (deliveryId) => {
  try {
    const res = await axiosInstance.get(
      `/projects/inventory/delivery/bom_delivery_laydowns?delivery_id=${deliveryId}`
    );

    if (res && res.data) {
      return {
        data: Array.isArray(res.data) ? res.data : [res.data],
        status: res.status,
        statusText: res.statusText,
      };
    }

    return { data: [] };
  } catch (error) {
    console.error("❌ Error fetching BOM delivery laydowns:", error);
    throw error;
  }
};

export const saveDeliveryWithSequence = async (deliveryData) => {
  try {
    const documentArray = deliveryData.document || [];

    const deliveryPayload = {
      name: deliveryData.name,
      comments: deliveryData.comments,
      date_time: deliveryData.date_time,
      document: documentArray,
    };

    let deliveryResponse;
    let deliveryId;
    let isEditMode = false;

    if (
      deliveryData.id &&
      deliveryData.id !== null &&
      deliveryData.id !== undefined
    ) {
      deliveryResponse = await updateDelivery(deliveryData.id, deliveryPayload);
      deliveryId = deliveryData.id;
      isEditMode = true;
    } else {
      deliveryResponse = await createDelivery(deliveryPayload);
      deliveryId = deliveryResponse.data;
    }

    if (deliveryData.items && deliveryData.items.length > 0) {
      const validItems = deliveryData.items.filter(
        (item) => item.laydown_id && item.bom_id
      );

      if (validItems.length === 0) {
        console.warn("No valid items provided for processing");
        return {
          success: true,
          deliveryId,
          message: "Delivery saved but no valid items to process",
        };
      }

      if (isEditMode) {
        const existingDeliveryLaydowns = await getDeliveryLaydown(deliveryId);

        const itemsByLaydown = validItems.reduce((acc, item) => {
          const laydownId = item.laydown_id;
          if (!acc[laydownId]) {
            acc[laydownId] = [];
          }
          acc[laydownId].push(item);
          return acc;
        }, {});

        for (const [laydownId, laydownItems] of Object.entries(
          itemsByLaydown
        )) {
          const existingDeliveryLaydown = existingDeliveryLaydowns.data?.find(
            (dl) => dl.laydown_id === parseInt(laydownId)
          );

          let deliveryLaydownId;

          if (existingDeliveryLaydown) {
            deliveryLaydownId = existingDeliveryLaydown.id;
          } else {
            const deliveryLaydownPayload = {
              delivery_id: deliveryId,
              laydown_id: parseInt(laydownId),
            };

            const deliveryLaydownResponse = await createDeliveryLaydown(
              deliveryLaydownPayload
            );
            deliveryLaydownId = deliveryLaydownResponse.data;
          }

          const inventoryPayload = laydownItems.map((item) => ({
            bom_id: item.bom_id,
            count: parseInt(item.delivered_qty) || 0,
            time: Math.floor(Date.now() / 1000),
            damaged: parseInt(item.damaged_qty) || 0,
            purpose_code: 0,
            comments: "",
          }));

          if (inventoryPayload.length > 0) {
            const inventoryResponse = await updateInventory(
              parseInt(laydownId),
              deliveryLaydownId,
              deliveryId,
              inventoryPayload
            );

            if (inventoryResponse.data !== "completed") {
              console.warn(
                `⚠️ Unexpected inventory update response for laydown ${laydownId}:`,
                inventoryResponse.data
              );
            }
          }
        }
      } else {
        const itemsByLaydown = validItems.reduce((acc, item) => {
          const laydownId = item.laydown_id;
          if (!acc[laydownId]) {
            acc[laydownId] = [];
          }
          acc[laydownId].push(item);
          return acc;
        }, {});

        for (const [laydownId, laydownItems] of Object.entries(
          itemsByLaydown
        )) {
          const deliveryLaydownPayload = {
            delivery_id: deliveryId,
            laydown_id: parseInt(laydownId),
          };

          const deliveryLaydownResponse = await createDeliveryLaydown(
            deliveryLaydownPayload
          );
          const deliveryLaydownId = deliveryLaydownResponse.data;

          const inventoryPayload = laydownItems.map((item) => ({
            bom_id: item.bom_id,
            count: parseInt(item.delivered_qty) || 0,
            time: Math.floor(Date.now() / 1000),
            damaged: parseInt(item.damaged_qty) || 0,
            purpose_code: 0,
            comments: "",
          }));

          if (inventoryPayload.length > 0) {
            const inventoryResponse = await updateInventory(
              parseInt(laydownId),
              deliveryLaydownId,
              deliveryId,
              inventoryPayload
            );

            if (inventoryResponse.data !== "completed") {
              console.warn(
                `⚠️ Unexpected inventory update response for laydown ${laydownId}:`,
                inventoryResponse.data
              );
            }
          }
        }
      }
    }

    const updatedDeliveries = await getDeliveries();

    return {
      success: true,
      deliveryId,
      updatedDeliveries: updatedDeliveries.data,
    };
  } catch (error) {
    console.error("❌ Error in delivery save sequence:", error);
    throw error;
  }
};

export const handleCustomResponsesInEdit = async (
  deliveryId,
  customResponses
) => {
  if (customResponses && Object.keys(customResponses).length > 0) {
    try {
      const customResponsesArray = Object.entries(customResponses).map(
        ([fieldName, value]) => ({
          field_name: fieldName,
          value: value,
        })
      );

      await saveCustomResponses(deliveryId, customResponsesArray);
    } catch (error) {
      console.error("❌ Error saving custom responses:", error);
    }
  }
};

export const getDeliveryLaydown = async (deliveryId) => {
  const res = await axiosInstance.get(
    `/projects/delivery-laydown?delivery_id=${deliveryId}`
  );
  return res;
};

export const getDeliveryById = async (id) => {
  const res = await axiosInstance.get(`/projects/delivery?delivery_id=${id}`);
  return res;
};

export const updateDelivery = async (id, data) => {
  if (!id || id === undefined || id === null) {
    throw new Error("Invalid delivery ID provided for update");
  }

  const res = await axiosInstance.post(
    `/projects/delivery?delivery_id=${id}`,
    data
  );
  return res;
};

export const deleteDelivery = async (id) => {
  const res = await axiosInstance.delete(
    `/projects/delivery?delivery_id=${id}`
  );
  return res;
};

export const getLaydowns = async () => {
  const res = await axiosInstance.get(`/projects/laydown`);
  return res;
};

export const getLaydownById = async (id) => {
  const res = await axiosInstance.get(`/projects/laydown?laydown_id=${id}`);
  return res;
};

export const createLaydown = async (data) => {
  const res = await axiosInstance.post(`/projects/laydown`, data);
  return res;
};

export const updateLaydown = async (id, data) => {
  const res = await axiosInstance.put(
    `/projects/laydown?laydown_id=${id}`,
    data
  );
  return res;
};

export const deleteLaydown = async (id) => {
  const res = await axiosInstance.delete(
    `/api/projects/laydown?laydown_id=${id}`
  );
  return res;
};

export const getParts = async () => {
  const res = await axiosInstance.get(`/projects/parts`);
  return res;
};

export const getPartsByLaydown = async (laydownId) => {
  const res = await axiosInstance.get(
    `/projects/activitymilestone?laydown_id=${laydownId}`
  );

  if (res?.data) {
    return res?.data;
  }

  return res;
};

export const uploadFile = async (file, fileName) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("name", fileName);

  const res = await axiosInstance.post(
    `/file_upload/upload/image/document`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
  return res;
};

export const getDeliveryFormSetup = async () => {
  const res = await axiosInstance.get(`/projects/delivery/custom_inputs`);
  return res;
};

export const updateDeliveryFormSetup = async (data, formName = "delivery") => {
  const fieldsArray = Array.isArray(data) ? data : [data];

  try {
    // For bulk operations, we need to send multiple requests since API requires
    // field_name and field_alias_name in query params for each field
    const results = [];

    for (const field of fieldsArray) {
      const fieldName =
        field.name?.toLowerCase().replace(/\s+/g, "_") || "custom_field";
      const fieldAliasName = field.name || "Custom Field";

      const queryParams = new URLSearchParams({
        form_name: formName,
        field_name: fieldName,
        field_alias_name: fieldAliasName,
      });

      const res = await axiosInstance.post(
        `/projects/delivery/custom_inputs?${queryParams.toString()}`,
        [field] // Send as array with single item
      );
      results.push(res);
    }

    return results.length === 1 ? results[0] : results;
  } catch (error) {
    console.error("Error saving custom fields:", error);
    throw error;
  }
};

export const saveCustomField = async (fieldData, formName = "delivery") => {
  return updateDeliveryFormSetup([fieldData], formName);
};

export const updateCustomField = async (
  fieldId,
  fieldData,
  formName = "delivery"
) => {
  try {
    const fieldName = fieldData.name.toLowerCase().replace(/\s+/g, "_");
    const fieldAliasName = fieldData.name;

    const requestBody = {
      type: fieldData.type,
      name: fieldAliasName,
    };

    const res = await axiosInstance.put(
      `/projects/delivery/custom_inputs/${fieldId}?form_name=${formName}&field_name=${fieldName}&field_alias_name=${fieldAliasName}`,
      requestBody
    );

    return res;
  } catch (error) {
    console.error(`Error updating field ${fieldData.name}:`, error);
    throw error;
  }
};

export const deleteCustomField = async (fieldId) => {
  try {
    const res = await axiosInstance.delete(
      `/projects/delivery/custom_inputs/delete/field_id=${fieldId}`
    );
    return res;
  } catch (error) {
    console.error(`Error deleting field with ID ${fieldId}:`, error);
    throw error;
  }
};

export const getCustomResponsesByDeliveryId = async (deliveryId) => {
  const res = await axiosInstance.get(
    `/projects/delivery/custom_responses?delivery_id=${deliveryId}`
  );
  return res;
};

// export const saveFieldAlias = async (formName, fieldName, fieldAliasName) => {
//   const res = await axiosInstance.post(
//     `/projects/save_field_alias?form_name=${formName}&field_name=${fieldName}&field_alias_name=${fieldAliasName}`
//   );
//   return res;
// };

export const getFieldAlias = async (formName, fieldName) => {
  const res = await axiosInstance.get(
    `/projects/get_field_alias?form_name=${formName}&field_name=${fieldName}`
  );
  return res;
};

export const getLaydownCountAgg = async (bomId) => {
  const res = await axiosInstance.get(
    `/projects/inventory/laydown-count/agg?bom_id=${bomId}`
  );
  return res;
};

export const getRemediatedCountBomDeliveryLaydown = async (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const res = await axiosInstance.get(
    `/projects/inventory/remediated-count/bom-delivery-laydown?${queryString}`
  );
  return res;
};

export const getLaydownMapData = async () => {
  // let res = await axiosInstance.get(`/tracker_row/get_scope_for_given_laydown?laydown_id=${laydownId}`)
  let res = await axiosInstance.get(`/tracker_row/get_scope_laydown_wise`);
  return res;
};
export const getInventoryByLaydown = async (laydownId) => {
  const res = await axiosInstance.get(
    `/projects/inventory/laydown/${laydownId}`
  );
  return res;
};

export const downloadDeliveryData = async (params = {}) => {
  const res = await axiosInstance.get(`/projects/delivery/download`, {
    params,
    responseType: "blob",
  });
  return res;
};

export const convertFromTimestamp = (timestamp, tz = 'Pacific/Auckland') => {
  const date = dayjs.unix(timestamp).tz(tz);
  return {
    date: date.format('DD/MM/YYYY'),
    time: date.format('hh:mm A'),
    timezone: tz
  };
};

export const getInventory = async (id) => {
  const res = await axiosInstance.get(
    `/laydown-scope/laydown/auto-manage-scope?laydown_id=${id}&is_download=True`
  );
  return res;
};

export const updateScope = async (data) => {
  let blockIds = {};
  if (data?.blockIds) {
    blockIds = Object.fromEntries(data?.blockIds.map((id) => [id, -1]));
  }
  await axiosInstance.post(
    `/tracker_row/update_laydown_id_for_tracker_rows?laydown_id=${data?.laydownId}`,
    blockIds
  );
};

export const getScopeLaydownWise = async () => {
  let res = await axiosInstance.get(`/tracker_row/get_scope_laydown_wise`);
  return res;
};